import React from 'react';
import { styled, keyframes, css } from 'styled-components';

import { Media } from '../../theme/Media.tsx';
import type { IActionsListItem } from './ActionListItem.tsx';
import ActionsListItem from './ActionListItem.tsx';
import type { IActionsList } from './ActionsList.tsx';
import ActionsList from './ActionsList.tsx';
import type { IAdditionalRow } from './AdditionalRow.tsx';
import AdditionalRow from './AdditionalRow.tsx';
import { TableProvider } from './context.tsx';
import { Field } from './Field.tsx';
import type { ITableFooter } from './Footer.tsx';
import Footer from './Footer.tsx';
import type { IMobileLabel } from './MobileLabel.tsx';
import MobileLabel from './MobileLabel.tsx';
import PositionsGroupTbodyFooter from './PositionsGroup/TbodyFooter.tsx';
import type { ITbodyHeader as IPositionsGroupTbodyHeader } from './PositionsGroup/TbodyHeader.tsx';
import PositionsGroupTbodyHeader, { Header as TbodyHeader } from './PositionsGroup/TbodyHeader.tsx';
import type { IScrollableWrapper } from './ScrollableWrapper.tsx';
import ScrollableWrapper from './ScrollableWrapper.tsx';
import type { ITableInfo } from './TableInfo.tsx';
import TableInfo from './TableInfo.tsx';
import type { ITbody } from './Tbody.tsx';
import Tbody, { Wrapper as TbodyWrapper } from './Tbody.tsx';
import TbodySeparator, { Separator as TbodySeparatorWrapper } from './TbodySeparator.tsx';
import type { ITd } from './Td.tsx';
import Td, { Wrapper as TdWrapper } from './Td.tsx';
import type { ITh } from './Th.tsx';
import Th from './Th.tsx';
import Tr, { Wrapper as TrWrapper } from './Tr.tsx';

const loading = keyframes`
  to {
    transform:translateX(100%)
  }
`;

const LoaderBar = styled.span`
  width: 100%;
  height: 18px;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grey10_1};
  &:after {
    animation: ${loading} 1.5s infinite;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(-100%);
    z-index: 1;
    background: linear-gradient(90deg, transparent, hsla(0, 0%, 100%, 0.3), transparent);
    animation-delay: 0.1s;
  }
`;

interface ILoader {
  columnsLength: number;
  rows?: number;
  dataTestId?: string;
  tdProps?: Partial<ITd>;
}

const Loader: React.FC<ILoader> = ({ columnsLength, rows = 3, dataTestId, tdProps }) => (
  <tbody data-testid={dataTestId}>
    {[...Array(rows)]
      .map((v1, i) => i)
      .map((i) => (
        <Tr key={`loader_tr_${i}`}>
          {[...Array(columnsLength)]
            .map((v2, j) => j)
            .map((j) => (
              <Td {...tdProps} key={`loader_td_${j}`}>
                <LoaderBar />
              </Td>
            ))}
        </Tr>
      ))}
  </tbody>
);

export type TableFC<P = Record<string, unknown>> = React.FC<P> & {
  Tr: typeof Tr;
  Th: React.FC<ITh>;
  Td: React.FC<ITd>;
  MobileLabel: React.FC<IMobileLabel>;
  Tbody: React.FC<ITbody>;
  TbodySeparator: typeof TbodySeparator;
  Footer: React.FC<ITableFooter>;
  Loader: React.FC<ILoader>;
  ActionsList: React.FC<IActionsList>;
  ActionsListItem: React.FC<IActionsListItem>;
  TableInfo: React.FC<ITableInfo>;
  AdditionalRow: React.FC<IAdditionalRow>;
  LoaderBar: typeof LoaderBar;
  PositionsGroupTbodyHeader: React.FC<IPositionsGroupTbodyHeader>;
  PositionsGroupTbodyFooter: typeof PositionsGroupTbodyFooter;
  Field: React.FC<React.PropsWithChildren>;
  ScrollableWrapper: React.FC<IScrollableWrapper>;
};

interface IWrapper {
  type?: 'sub' | 'withGroups';
  $isCollapsedNav?: boolean;
}

export const Wrapper = styled.table<IWrapper>`
  width: 100%;
  font-size: 13px;
  > thead {
    + ${TbodySeparatorWrapper} {
      display: none;
    }
  }
  ${({ type, $isCollapsedNav, theme }) =>
    type === 'withGroups' &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        > tbody._in-group:not(._is-open) > tr > td:first-child {
          border-left: 2px solid ${theme.colors.grey30_1};
        }
        > tbody._in-group:not(._is-open) > tr > td:last-child {
          border-right: 2px solid ${theme.colors.grey30_1};
        }
      }
      @media ${$isCollapsedNav ? Media.mdMax : Media.lgMax} {
        > tbody._in-group:not(._is-open) {
          position: relative;
          &:before {
            content: '';
            display: block;
            position: absolute;
            top: -5px;
            bottom: -5px;
            left: -5px;
            right: -5px;
            background-color: ${theme.colors.grey30_1};
          }
          > tr {
            position: relative;
            background-color: ${theme.colors.grey00_1};
          }
        }
      }
    `}
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    flex-wrap: wrap;
    thead {
      display: flex;
      width: 100%;
      tr {
        width: 100%;
        margin-bottom: 0;
      }
    }
    tbody:not(${TbodyWrapper}):not(${TbodyHeader}) {
      display: flex;
      flex-wrap: wrap;
      margin: ${({ type }) => type === 'sub' && '10px 10px 0'};
      width: ${({ type }) => (type === 'sub' ? 'calc(100% - 20px)' : '100%')};
    }
    tbody {
      ${({ type, theme }) =>
        type === 'sub' &&
        css`
          ${TrWrapper} {
            ${TdWrapper} {
              background: transparent;
            }
            background: ${theme.colors.grey00_1};
            border: 1px solid ${theme.colors.grey10_1};
            border-radius: 4px;
            ${theme.shadows.shadowTuna};
          }
        `}
    }
  }
`;

export interface ITable {
  children?: React.ReactNode;
  className?: string;
  dataTestId?: string;
  type?: 'sub' | 'withGroups';
  isCollapsedNav?: boolean;
}

const Table: React.FC<ITable> = ({ children, className, dataTestId, type, isCollapsedNav = true }) => (
  <TableProvider isCollapsedNav={isCollapsedNav}>
    <Wrapper data-testid={dataTestId} className={className} type={type} $isCollapsedNav={isCollapsedNav}>
      {children}
    </Wrapper>
  </TableProvider>
);

const TableMemo = React.memo(Table);

const TableWrapper: TableFC<ITable> = (props) => <TableMemo {...props} />;

TableWrapper.Tr = Tr;
TableWrapper.Th = Th;
TableWrapper.Td = Td;
TableWrapper.MobileLabel = MobileLabel;
TableWrapper.Tbody = Tbody;
TableWrapper.Footer = Footer;
TableWrapper.Loader = Loader;
TableWrapper.LoaderBar = LoaderBar;
TableWrapper.ActionsList = ActionsList;
TableWrapper.ActionsListItem = ActionsListItem;
TableWrapper.TableInfo = TableInfo;
TableWrapper.AdditionalRow = AdditionalRow;
TableWrapper.TbodySeparator = TbodySeparator;
TableWrapper.PositionsGroupTbodyHeader = PositionsGroupTbodyHeader;
TableWrapper.PositionsGroupTbodyFooter = PositionsGroupTbodyFooter;
TableWrapper.Field = Field;
TableWrapper.ScrollableWrapper = ScrollableWrapper;

export default TableWrapper;
